import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import AuthDialog from "@/components/AuthDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/podcasts", label: "Podcasts" },
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=Headwear", label: "Hats" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [scores, setScores] = useState({ mlb: [], nhl: [], nfl: [] });
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => {
      api.get("/scores")
        .then((r) => setScores({ mlb: r.data.mlb || [], nhl: r.data.nhl || [], nfl: r.data.nfl || [] }))
        .catch(() => {});
    };
    load();
    // Refresh scores every 3 min
    const interval = setInterval(load, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const gameRecapUrl = (g) => {
    if (g.game_url) return g.game_url;
    const league = (g.league || "").toLowerCase();
    if (league === "mlb") return "https://www.espn.com/mlb/scoreboard";
    if (league === "nhl") return "https://www.espn.com/nhl/scoreboard";
    if (league === "nfl") return "https://www.espn.com/nfl/scoreboard";
    return "https://www.espn.com/";
  };

  const renderMarqueeContent = () => {
    const allGames = [
      ...scores.mlb.map((g) => ({ ...g, color: "text-halo-red", emoji: "⚾" })),
      ...scores.nhl.map((g) => ({ ...g, color: "text-[#F47A38]", emoji: "🏒" })),
      ...scores.nfl.map((g) => ({ ...g, color: "text-halo-cream", emoji: "🏈" })),
    ];
    return (
      <div className="flex items-center gap-6 px-6 shrink-0">
        <span className="text-halo-red font-bold">● TODAY'S SCORES</span>
        {allGames.length > 0 ? (
          allGames.map((g, idx) => {
            const state = (g.state || "").toString().trim();
            const detail = (g.detail || "").toString();
            const isFinal = ["Final", "final", "FINAL", "post", "OFF"].some((s) => state.includes(s));
            const isLive = !isFinal && (state === "in" || ["Live", "In Progress", "LIVE", "CRIT"].some((s) => state.includes(s) || detail.includes(s)));
            const isScheduled = !isFinal && !isLive && ["FUT", "PRE", "pre", "Preview", "Scheduled", "SCHED", "sched", "Pre-Game"].some((s) => state.includes(s) || detail.includes(s));
            const scoreStr = (g.away_score !== null && g.home_score !== null && g.away_score !== undefined && g.home_score !== undefined)
              ? `${g.away} ${g.away_score} – ${g.home_score} ${g.home}`
              : `${g.away} @ ${g.home}`;
            return (
              <React.Fragment key={`${g.league}-${idx}`}>
                <a
                  href={gameRecapUrl(g)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`marquee-game-${g.league}-${idx}`}
                  className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
                  title={`${isLive ? "Live" : isFinal ? "Recap" : "Preview"} on ESPN`}
                >
                  <span className={`${g.color} font-bold`}>{g.emoji} {g.league}</span>
                  <span className="text-halo-cream">{scoreStr}</span>
                  <span className={`text-[10px] ${isLive ? "text-halo-red" : "text-halo-cream/60"}`}>
                    {isLive ? "· LIVE" : isFinal ? "· FINAL" : isScheduled ? "· SCHED" : `· ${detail || "SCHED"}`}
                  </span>
                </a>
                <span className="text-halo-red/60">•</span>
              </React.Fragment>
            );
          })
        ) : (
          <>
            <span className="text-halo-cream/70">No games right now — check back at gametime</span>
            <span className="text-halo-red/60">•</span>
            <span className="text-halo-cream">HITI Network · Postgame Show Nightly</span>
            <span className="text-halo-red/60">•</span>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-halo-navy text-halo-cream overflow-hidden border-b border-halo-redHover" data-testid="marquee-bar">
        <div className="flex whitespace-nowrap animate-marquee py-2 font-sports text-xs sm:text-sm tracking-widest uppercase">
          {[0, 1].map((i) => (
            <React.Fragment key={i}>{renderMarqueeContent()}</React.Fragment>
          ))}
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F8F8F5]/90 border-b border-border" data-testid="site-header">
        <div className="px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between h-24">
            <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
              <img
                src="https://customer-assets.emergentagent.com/job_halo-infield/artifacts/elck0w48_481151224_546514117820888_298116822983296389_n.jpg"
                alt="HITI Network"
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
              />
              <div className="hidden sm:block leading-[0.95]">
                <div className="font-sports text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight text-halo-navy">Halos in the</div>
                <div className="font-sports text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight text-halo-red">Infield</div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-10">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-sports uppercase tracking-wider text-sm transition-colors ${
                      isActive ? "text-halo-red" : "text-halo-navy hover:text-halo-red"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/cart")}
                data-testid="cart-button"
                className="relative p-2 hover:text-halo-red transition-colors"
                aria-label="Cart"
              >
                <ShoppingBag className="w-5 h-5" strokeWidth={2.5} />
                {count > 0 && (
                  <span
                    data-testid="cart-count"
                    className="absolute -top-1 -right-1 bg-halo-red text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center"
                  >
                    {count}
                  </span>
                )}
              </button>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 p-2 hover:text-halo-red">
                    <div className="w-8 h-8 bg-halo-navy text-halo-cream font-sports flex items-center justify-center">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none border-2 border-halo-navy">
                    <DropdownMenuLabel className="font-sports uppercase">{user.name}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="font-body text-xs text-muted-foreground">{user.email}</DropdownMenuItem>
                    {user.is_admin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-link" className="cursor-pointer font-sports uppercase tracking-wide text-halo-red">
                          <Settings className="w-4 h-4 mr-2" />Admin Panel
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="cursor-pointer font-sports uppercase tracking-wide">
                      <LogOut className="w-4 h-4 mr-2" />Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  data-testid="signin-btn"
                  onClick={() => setAuthOpen(true)}
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border-2 border-halo-navy text-halo-navy font-sports uppercase tracking-wider text-xs hover:bg-halo-navy hover:text-halo-cream transition-colors"
                >
                  <User className="w-4 h-4" strokeWidth={2.5} /> Sign In
                </button>
              )}

              <button
                className="md:hidden p-2"
                data-testid="mobile-menu-btn"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label="Menu"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <nav className="md:hidden py-4 border-t border-border flex flex-col gap-3">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `font-sports uppercase tracking-wider text-base py-2 ${isActive ? "text-halo-red" : "text-halo-navy"}`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              {!user && (
                <button
                  onClick={() => { setMobileOpen(false); setAuthOpen(true); }}
                  data-testid="mobile-signin-btn"
                  className="self-start px-4 py-2 border-2 border-halo-navy text-halo-navy font-sports uppercase text-xs"
                >
                  Sign In
                </button>
              )}
            </nav>
          )}
        </div>
      </header>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
